import { IDatasource, Schema } from "@cak-legacy/generation-core";
import * as H from "history";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { BevestigPagina } from "../Components/BevestigPagina";
import { FormulierPagina } from "../Components/FormulierPagina";
import { FormBaseComponentState } from "../Interfaces/form-base-component-state";
import { translate } from "../Locales/translator";

export interface IFormBaseComponentProps<T, X> {
  getSchema: (disableAttachments?: boolean) => Schema;
  handleSubmit: (values: T) => Promise<X>;
  submitConfirmationText?: string;
  /**
   * @deprecated use bevestigPagina.getAdditionalMessage instead
   */
  meldingNoteerNummer?: string;
  defaultValues: T;
  datasources?: IDatasource[];
  handleReset: () => T;
  onCancelForm?: () => void;
  renderFormSubmitted: (values: T) => JSX.Element;
  pageBaseUrl: string;
  introText?: JSX.Element;
  introConfirmPageText?: JSX.Element;
  onShowButtons?: (values: T) => boolean;
  mapResponse: (values: X) => X;
  preserveUrl?: boolean;
  bevestigPagina?: {
    primaryButtonClassName?: string;
    secondaryButtonClassName?: string;
    primaryButtonText?: string;
    skipEditPage?: boolean;
    showCancelButtonIfEditPageIsSkipped?: boolean;
    getAdditionalMessage?: (values?: T) => JSX.Element | string | undefined;
  };
  backToFormText?: string;
}

type LocationType<T> = {
  values: T;
  formState: FormBaseComponentState;
  sessionId: number;
};

const goBackInBrowserHistory = (
  event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  history: H.History<unknown>
) => {
  event.preventDefault();
  history.goBack();
  return false;
};

const updateUrlInBrowser = <T extends unknown>(
  formStepUrlPath: string,
  formState: FormBaseComponentState,
  history: H.History<unknown>,
  location: H.Location<LocationType<T>>,
  pageBaseUrl: string,
  values: T | undefined,
  sessionId: number
) => {
  if (location.state && formState !== location.state.formState) {
    history.push(`${pageBaseUrl}/${formStepUrlPath}`, {
      ...location.state,
      values,
      formState,
      sessionId
    });
  }
};

export const FormBaseComponent = <T extends unknown, X extends unknown>(
  props: IFormBaseComponentProps<T, X>
) => {
  const [formState, setFormState] = React.useState<FormBaseComponentState>(
    props.bevestigPagina?.skipEditPage ? FormBaseComponentState.ConfirmForm : FormBaseComponentState.EnterData
  );
  const [values, setValues] = React.useState<T | undefined>(
    props.bevestigPagina?.skipEditPage ? props.defaultValues : undefined
  );
  const [errorMessage, setErrorMessage] = React.useState("");
  const history = useHistory();
  const location = useLocation<LocationType<T>>();
  const [sessionId, setSessionId] = React.useState(0);
  const [hasConfirmed, setConfirmed] = React.useState(false);

  React.useEffect(() => {
    if (window && window.scrollTo) {
      window.scrollTo({
        top: 0
      });
    }
  }, [formState]);

  const updateUrl = (formStepUrlPath: string) => {
    if (props.preserveUrl === true) {
      return;
    }
    updateUrlInBrowser<T>(
      formStepUrlPath,
      formState,
      history,
      location,
      props.pageBaseUrl,
      values,
      sessionId
    );
  };

  const gotoEnterFormState = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (props.preserveUrl === true) {
      if (props.bevestigPagina?.skipEditPage) {
        setFormState(FormBaseComponentState.ConfirmForm);
      } else {
        setFormState(FormBaseComponentState.EnterData);
      }
      return;
    }
    goBackInBrowserHistory(event, history);
  };

  const cancelForm = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    if (props.onCancelForm) {
      props.onCancelForm();
    }
    return false;
  };

  const isCurrentStateFormWasSendAndNextStateEnterData = () => {
    if (props.bevestigPagina?.skipEditPage) {
      return (
        location.state.formState === FormBaseComponentState.ConfirmForm &&
        formState === FormBaseComponentState.FormWasSend
      );
    }
    return (
      location.state.formState === FormBaseComponentState.EnterData &&
      formState === FormBaseComponentState.FormWasSend
    );
  };

  React.useEffect(() => {
    if (!location.state || props.preserveUrl === true) {
      return;
    }
    if (
      isCurrentStateFormWasSendAndNextStateEnterData() ||
      (sessionId > 0 && sessionId < location.state.sessionId)
    ) {
      setValues(props.handleReset());
      setFormState(
        props.bevestigPagina?.skipEditPage
          ? FormBaseComponentState.ConfirmForm
          : FormBaseComponentState.EnterData
      );
      setSessionId(sessionId + 1);
      return;
    } else if (location.state.formState === FormBaseComponentState.FormWasSend) {
      if (
        props.bevestigPagina?.skipEditPage
          ? formState === FormBaseComponentState.ConfirmForm
          : formState === FormBaseComponentState.EnterData
      ) {
        history.replace(props.pageBaseUrl, {
          formState: props.bevestigPagina?.skipEditPage
            ? FormBaseComponentState.ConfirmForm
            : FormBaseComponentState.EnterData,
          sessionId
        });
        return;
      }
    }
    setValues(location.state.values);
    setFormState(location.state.formState);
  }, [location, props.preserveUrl]);

  React.useEffect(() => {
    switch (formState) {
      case FormBaseComponentState.ConfirmForm:
        updateUrl("bevestigen");
        break;
      case FormBaseComponentState.FormSendFailed:
        updateUrl("fout-tijdens-verzenden");
        break;
      case FormBaseComponentState.FormWasSend:
        if (!props.preserveUrl) {
          history.replace("verzonden", {
            values,
            formState: FormBaseComponentState.FormWasSend
          });
        }
        setValues(props.handleReset());
        break;
    }
  }, [formState, props.preserveUrl]);

  if (formState === FormBaseComponentState.FormWasSend) {
    return (
      <>
        {props.renderFormSubmitted(
          props.preserveUrl || props.bevestigPagina?.skipEditPage
            ? (values as unknown as T)
            : location.state.values
        )}
      </>
    );
  }

  if (formState === FormBaseComponentState.FormSendFailed) {
    return (
      <>
        <div className="alert alert-danger error-text mt-4" role="alert">
          {translate("ErrorFormSubmit", { errorMessage: errorMessage })}
        </div>
        <br />
        <button
          className={`btn ${props.bevestigPagina?.secondaryButtonClassName ?? "btn-secondary"}`}
          onClick={(event) => gotoEnterFormState(event)}
        >
          {props.backToFormText ?? translate("TerugNaarHetFormulier")}
        </button>
      </>
    );
  }

  if (formState === FormBaseComponentState.ConfirmForm || formState === FormBaseComponentState.SendingForm) {
    return (
      <BevestigPagina
        formState={formState}
        getSchema={props.getSchema}
        gotoEnterFormState={gotoEnterFormState}
        handleSubmit={props.handleSubmit}
        hasConfirmed={hasConfirmed}
        setConfirmed={setConfirmed}
        submitConfirmationText={props.submitConfirmationText}
        additionalMessage={props.bevestigPagina?.getAdditionalMessage?.(values) ?? props.meldingNoteerNummer}
        setErrorMessage={setErrorMessage}
        setFormState={setFormState}
        setValues={setValues}
        values={values}
        introConfirmPageText={props.introConfirmPageText}
        introText={props.introText}
        mapResponse={props.mapResponse}
        primaryButtonClassName={props.bevestigPagina?.primaryButtonClassName}
        secondaryButtonClassName={props.bevestigPagina?.secondaryButtonClassName}
        primaryButtonText={props.bevestigPagina?.primaryButtonText}
        skipEditPageAndShowConfirmationOnly={props.bevestigPagina?.skipEditPage}
        showCancelButtonIfEditPageIsSkipped={props.bevestigPagina?.showCancelButtonIfEditPageIsSkipped}
        onCancelForm={cancelForm}
      />
    );
  }

  return (
    <FormulierPagina<T>
      defaultValues={props.defaultValues}
      getSchema={props.getSchema}
      handleReset={props.handleReset}
      history={history}
      sessionId={sessionId}
      setFormState={setFormState}
      setSessionId={setSessionId}
      setValues={setValues}
      values={values}
      datasources={props.datasources}
      introConfirmPageText={props.introConfirmPageText}
      introText={props.introText}
      onShowButtons={props.onShowButtons}
    />
  );
};
